import { useMutation, useQuery } from '@tanstack/react-query';

import postReviewMeetingService from './postReviewMeetingService';

import type {
    MeetingAgenda,
    MeetingAgendaUpdatePayload,
    MeetingCompletionStatus,
    MeetingCompletionUpdateResponse,
    MeetingTranscript,
} from '@/types/review/PostReviewMeeting';

import { queryClient } from '@/AppWithProviders';
import {
    PostReviewMeetingMutationKey,
    PostReviewMeetingQueryKey,
    ReviewQueryKey,
} from '@/constants/QueryKeys';

export const useGetMeetingStatus = (reviewInstanceId?: string) => {
    return useQuery<MeetingCompletionStatus, Error>({
        queryKey: [PostReviewMeetingQueryKey.MEETING_STATUS, reviewInstanceId],
        queryFn: () => postReviewMeetingService.getMeetingStatus(reviewInstanceId!),
        enabled: !!reviewInstanceId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useCompleteMeeting = (reviewInstanceId: string) => {
    return useMutation<MeetingCompletionUpdateResponse, Error, void>({
        mutationKey: [PostReviewMeetingMutationKey.COMPLETE_MEETING],
        mutationFn: () => postReviewMeetingService.completeMeeting(reviewInstanceId),
        onSuccess: (data) => {
            // The PUT returns the full meeting status, so seed the cache directly instead of refetching it.
            queryClient.setQueryData(
                [PostReviewMeetingQueryKey.MEETING_STATUS, reviewInstanceId],
                data,
            );
            // The paged review lists aren't carried in this response, so they still need a refetch.
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.ONGOING_REVIEWS_PAGED],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.PAST_REVIEWS_PAGED],
            });
        },
    });
};

export const useUncompleteMeeting = (reviewInstanceId: string) => {
    return useMutation<MeetingCompletionUpdateResponse, Error, void>({
        mutationKey: [PostReviewMeetingMutationKey.UNCOMPLETE_MEETING],
        mutationFn: () => postReviewMeetingService.uncompleteMeeting(reviewInstanceId),
        onSuccess: (data) => {
            // The PUT returns the full meeting status, so seed the cache directly instead of refetching it.
            queryClient.setQueryData(
                [PostReviewMeetingQueryKey.MEETING_STATUS, reviewInstanceId],
                data,
            );
            // The paged review lists aren't carried in this response, so they still need a refetch.
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.CYCLE_INSTANCE],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.ONGOING_REVIEWS_PAGED],
            });
            queryClient.invalidateQueries({
                queryKey: [ReviewQueryKey.PAST_REVIEWS_PAGED],
            });
        },
    });
};

// The agenda is keyed to the cycle. A null result means "not generated yet" (the component then triggers
// AI generation); it is not an error state. A first read creates the agenda in the reader's language (the
// Accept-Language header); the language is not part of the key, since an agenda keeps its own whoever reads it.
export const useGetMeetingAgenda = (cycleInstanceId?: string) => {
    return useQuery<MeetingAgenda | null, Error>({
        queryKey: [PostReviewMeetingQueryKey.MEETING_AGENDA, cycleInstanceId],
        queryFn: () => postReviewMeetingService.getMeetingAgenda(cycleInstanceId!),
        enabled: !!cycleInstanceId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useUpdateMeetingAgenda = (cycleInstanceId: string) => {
    return useMutation<MeetingAgenda, Error, MeetingAgendaUpdatePayload>({
        mutationKey: [PostReviewMeetingMutationKey.UPDATE_MEETING_AGENDA],
        mutationFn: (payload) =>
            postReviewMeetingService.updateMeetingAgenda(cycleInstanceId, payload),
        onSuccess: (data) => {
            queryClient.setQueryData(
                [PostReviewMeetingQueryKey.MEETING_AGENDA, cycleInstanceId],
                data,
            );
        },
    });
};

// The transcript is cycle-keyed. A null result means nothing has been uploaded yet.
export const useGetMeetingTranscript = (cycleInstanceId?: string) => {
    return useQuery<MeetingTranscript | null, Error>({
        queryKey: [PostReviewMeetingQueryKey.MEETING_TRANSCRIPT, cycleInstanceId],
        queryFn: () => postReviewMeetingService.getMeetingTranscript(cycleInstanceId!),
        enabled: !!cycleInstanceId,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useUploadMeetingTranscript = (cycleInstanceId: string) => {
    return useMutation<MeetingTranscript, Error, File>({
        mutationKey: [PostReviewMeetingMutationKey.UPLOAD_MEETING_TRANSCRIPT],
        mutationFn: (file) =>
            postReviewMeetingService.uploadMeetingTranscript(cycleInstanceId, file),
        onSuccess: (data) => {
            queryClient.setQueryData(
                [PostReviewMeetingQueryKey.MEETING_TRANSCRIPT, cycleInstanceId],
                data,
            );
        },
    });
};
